import { useCallback, useEffect, useMemo, useState } from 'react';
import { PiLifebuoyLight, PiXLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import SearchableSelect, {
  type SearchableSelectOption,
} from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCompanies } from 'src/modules/shared/hooks/api/companies';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';

const SUPPORT_MODE_REDIRECT_PATH = '/communications';

export const SupportModeControls = () => {
  const {
    assumedTenantId,
    enableSupportMode,
    disableSupportMode,
    canUseSupportMode,
    loading,
  } = useSupportTenant();

  const {
    companies,
    loading: companiesLoading,
    error: companiesError,
    refetch: refetchCompanies,
  } = useCompanies({ enabled: canUseSupportMode });

  const [isExpanded, setIsExpanded] = useState(false);

  const { user: currentUser, loading: currentUserLoading } = useCurrentUser();
  const userTenantId = currentUser?.tenantId ?? null;

  const isLoadingState = loading || companiesLoading || currentUserLoading;

  const companyOptions: SearchableSelectOption[] = useMemo(() => {
    const filteredCompanies =
      userTenantId === null
        ? companies
        : companies.filter((company) => company.tenantId !== userTenantId);

    const options: SearchableSelectOption[] = filteredCompanies.map(
      (company) => ({
        value: company.tenantId,
        keywords: `${company.tenantId} ${company.name ?? ''}`.trim(),
        content: company.name ?? company.tenantId,
      })
    );

    return options;
  }, [companies, userTenantId]);

  const redirectToSupportModeLanding = useCallback(() => {
    window.location.assign(SUPPORT_MODE_REDIRECT_PATH);
  }, []);

  useEffect(() => {
    const isAssumingOwnTenant =
      assumedTenantId &&
      userTenantId &&
      assumedTenantId === userTenantId &&
      canUseSupportMode;

    if (isAssumingOwnTenant) {
      disableSupportMode();
      redirectToSupportModeLanding();
    }
  }, [
    assumedTenantId,
    userTenantId,
    disableSupportMode,
    canUseSupportMode,
    redirectToSupportModeLanding,
  ]);

  if (isLoadingState || !canUseSupportMode) {
    return null;
  }

  const isAssuming = Boolean(
    assumedTenantId && assumedTenantId !== userTenantId
  );

  const handleSelectCompany = (tenantId?: string) => {
    if (!tenantId) return;
    if (userTenantId && tenantId === userTenantId) return;

    enableSupportMode(tenantId);
    redirectToSupportModeLanding();
  };

  const appearanceClass = isExpanded
    ? 'bg-white rounded-full px-3 py-2 shadow-lg'
    : isAssuming
      ? 'bg-warning-100 text-warning-700 rounded-full pl-3 pr-4 py-2 shadow-sm'
      : 'rounded-full';

  const layoutClass = isExpanded
    ? 'flex-row-reverse gap-3'
    : isAssuming
      ? 'gap-2'
      : '';

  const containerClasses = `fixed bottom-[2rem] right-[2rem] z-[1000] flex items-center transition-all duration-200 ease-in-out ${appearanceClass} ${layoutClass}`;

  const triggerClasses = `flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
    isAssuming
      ? 'bg-warning-500 text-white hover:bg-warning-600'
      : 'bg-neutral-900 text-white hover:bg-neutral-800'
  }`;
  const showSelect = isExpanded;

  const activeCompany = companyOptions.find(
    (option) => option.value === assumedTenantId
  );
  const activeCompanyLabel =
    typeof activeCompany?.content === 'string'
      ? activeCompany.content
      : assumedTenantId;

  const tooltipContent = isAssuming
    ? `Modo soporte activo: ${activeCompanyLabel}`
    : isExpanded
      ? 'Cerrar'
      : 'Activa el modo soporte';

  const renderCompanyOption = (option: SearchableSelectOption) => {
    const companyName =
      typeof option.content === 'string' ? option.content : option.value;

    return (
      <div className="flex min-w-0 flex-col items-start text-left">
        <Text size="small" weight="medium" className="w-full truncate">
          {companyName}
        </Text>
        <Text
          size="tiny"
          color="secondary"
          className="w-full truncate !text-[11px] uppercase tracking-[0.04em] opacity-80"
        >
          {option.value}
        </Text>
      </div>
    );
  };

  return (
    <div
      data-testid="support-mode-controls"
      data-support-mode={isAssuming ? 'true' : 'false'}
      className={containerClasses}
    >
      <Tooltip
        content={tooltipContent}
        placement="top"
        className="whitespace-nowrap"
      >
        <button
          type="button"
          data-testid="support-mode-toggle"
          onClick={() => setIsExpanded((prev) => !prev)}
          className={triggerClasses}
          aria-label={tooltipContent}
        >
          {isExpanded ? (
            <PiXLight className="h-5 w-5" />
          ) : (
            <PiLifebuoyLight className="h-5 w-5" />
          )}
        </button>
      </Tooltip>

      {showSelect && (
        <div className="flex flex-1 items-center gap-2">
          <div className="min-w-[200px] flex-1">
            <SearchableSelect
              options={companyOptions}
              value={assumedTenantId ?? undefined}
              onChange={handleSelectCompany}
              placeholder="Selecciona un tenant..."
              size="md"
              loading={companiesLoading}
              disabled={isLoadingState}
              renderOption={renderCompanyOption}
            />
          </div>

          {companiesError && (
            <Button
              size="sm"
              appearance="ghost"
              variant="warning"
              onClick={() => void refetchCompanies()}
            >
              Reintentar
            </Button>
          )}

          {isAssuming && (
            <Button
              onClick={() => {
                disableSupportMode();
                redirectToSupportModeLanding();
              }}
              size="sm"
              appearance="ghost"
              variant="danger"
              data-testid="support-mode-exit"
              isIconOnly
            >
              <PiXLight className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
